import React, { useEffect, useRef } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { HeaderComponent } from "../../components/Header";
import {
  Container,
  Content,
  Layout,
  Sidebar,
  Panel,
  BackLink,
  AccessibilityStyles,
  SkeletonBox,
  SkeletonIcon,
  SkeletonNav,
  SkeletonNavRow,
  SkeletonTextGroup,
  SkeletonCard,
  SkeletonCardHeader,
  SkeletonFormGrid,
  SkeletonField,
  SkeletonBackLink,
} from "./styles";
import { SettingsNav } from "./components/SettingsNav";
import { ProfileForm } from "./ProfileForm";
import { PasswordForm } from "./PasswordForm";
import { PreferencesForm } from "./PreferencesForm";
import { AccountManagement } from "./AccountManagement";
import { Contact } from "./Contact";
import { ReportsSection } from "./ReportsSection";
import { GoalsSection } from "./GoalsSection";
import {
  SettingsPageProps,
  SettingsSectionId,
  SettingsSkeletonProps,
} from "./types";
import { ErrorState } from "../../components/States";
import { useSettingsNavigation } from "./hooks/useSettingsNavigation";
import { useSettingsActions } from "./hooks/useSettingsActions";
import { useSettingsData } from "./hooks/useSettingsData";
import {
  SECTIONS_WITHOUT_USER_DATA,
  SETTINGS_BASE_PATH,
  SETTINGS_SECTIONS,
  SETTINGS_SUBTITLE,
  SETTINGS_TITLE,
} from "./constants";
import { BREAKPOINTS } from "../../utils/constants";
import { useResponsive } from "../../hooks/useResponsive";

const SKELETON_FIELDS = [0, 1, 2, 3];

const SettingsSkeleton: React.FC<SettingsSkeletonProps> = ({
  isMobile,
  hasValidSection,
}) => {
  const showMenuOnly = isMobile && !hasValidSection;
  const showHeader = !(isMobile && hasValidSection);

  const navList = (
    <SkeletonNav aria-hidden="true">
      {SETTINGS_SECTIONS.map((section) => (
        <SkeletonNavRow key={section.id}>
          <SkeletonIcon />
          <SkeletonTextGroup>
            <SkeletonBox width="55%" height="1rem" />
            <SkeletonBox width="38%" height="0.75rem" />
          </SkeletonTextGroup>
        </SkeletonNavRow>
      ))}
    </SkeletonNav>
  );

  const panel = (
    <SkeletonCard aria-hidden="true">
      <SkeletonCardHeader>
        <SkeletonIcon />
        <SkeletonTextGroup>
          <SkeletonBox width="9rem" height="1.25rem" />
          <SkeletonBox width="14rem" height="0.875rem" />
        </SkeletonTextGroup>
      </SkeletonCardHeader>
      <SkeletonFormGrid>
        {SKELETON_FIELDS.map((field) => (
          <SkeletonField key={field}>
            <SkeletonBox width="6rem" height="0.75rem" />
            <SkeletonBox height="2.75rem" />
          </SkeletonField>
        ))}
      </SkeletonFormGrid>
    </SkeletonCard>
  );

  let body = (
    <Layout>
      <Sidebar>{navList}</Sidebar>
      <Panel>{panel}</Panel>
    </Layout>
  );

  if (showMenuOnly) {
    body = navList;
  } else if (isMobile) {
    body = (
      <>
        <SkeletonBackLink>
          <SkeletonBox width="6rem" height="1rem" />
        </SkeletonBackLink>
        {panel}
      </>
    );
  }

  return (
    <Container>
      {showHeader ? (
        <HeaderComponent
          title={SETTINGS_TITLE}
          subtitle={SETTINGS_SUBTITLE}
          showDivider={false}
        />
      ) : null}
      <Content
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading settings"
      >
        {body}
      </Content>
    </Container>
  );
};

export const SettingsPage: React.FC<SettingsPageProps> = ({ section }) => {
  const { userDetails, userPreferences, isLoading, error, refetch } =
    useSettingsData();
  const { handleUpdateProfile, handleUpdatePassword, handleUpdatePreferences } =
    useSettingsActions();

  const isMobile = useResponsive(BREAKPOINTS.tabletLarge);
  const { activeSection, hasValidSection, redirectToDefault } =
    useSettingsNavigation(section);

  const needsUserData = !SECTIONS_WITHOUT_USER_DATA.includes(activeSection);

  const saveFormRef = useRef<(() => void) | null>(null);
  const cancelFormRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isMobile && !hasValidSection) {
      redirectToDefault();
    }
  }, [isMobile, hasValidSection, redirectToDefault]);

  if (error && needsUserData) {
    return (
      <Container>
        <HeaderComponent title={SETTINGS_TITLE} subtitle={SETTINGS_SUBTITLE} />
        <Content>
          <ErrorState message={error} onRetry={refetch} />
        </Content>
      </Container>
    );
  }

  if ((isLoading || !userDetails || !userPreferences) && needsUserData) {
    return (
      <SettingsSkeleton isMobile={isMobile} hasValidSection={hasValidSection} />
    );
  }

  const renderSection = (current: SettingsSectionId) => {
    if (current === "reports") {
      return <ReportsSection />;
    }
    if (current === "goals") {
      return <GoalsSection />;
    }

    if (!userDetails || !userPreferences) {
      return null;
    }

    switch (current) {
      case "profile":
        return (
          <ProfileForm
            userDetails={userDetails}
            onUpdate={handleUpdateProfile}
            saveFormRef={saveFormRef}
            cancelFormRef={cancelFormRef}
          />
        );
      case "security":
        return (
          <PasswordForm
            onUpdate={handleUpdatePassword}
            saveFormRef={saveFormRef}
            cancelFormRef={cancelFormRef}
          />
        );
      case "preferences":
        return (
          <PreferencesForm
            userPreferences={userPreferences}
            onUpdate={handleUpdatePreferences}
          />
        );
      case "account":
        return <AccountManagement />;
      case "contact":
        return <Contact />;
      default:
        return null;
    }
  };

  const showMenuOnly = isMobile && !hasValidSection;
  const showHeader = !(isMobile && hasValidSection);

  return (
    <Container>
      <AccessibilityStyles />
      <a href="#settings-content" className="skip-to-content">
        Skip to settings content
      </a>
      {showHeader ? (
        <HeaderComponent
          title={SETTINGS_TITLE}
          subtitle={SETTINGS_SUBTITLE}
          showDivider={false}
        />
      ) : null}

      <Content id="settings-content">
        {showMenuOnly ? (
          <SettingsNav activeSection={activeSection} />
        ) : (
          <>
            {isMobile ? (
              <BackLink href={SETTINGS_BASE_PATH}>
                <ArrowBack />
                Settings
              </BackLink>
            ) : null}
            <Layout>
              {isMobile ? null : (
                <Sidebar>
                  <SettingsNav activeSection={activeSection} />
                </Sidebar>
              )}
              <Panel role="region" aria-label={`${activeSection} settings`}>
                {renderSection(activeSection)}
              </Panel>
            </Layout>
          </>
        )}
      </Content>
    </Container>
  );
};
